// src/Games/MathAdventure/MathAdventure.js
import React, { useState, useEffect } from "react";
import "./MathAdventure.css";
import warriorImg from "./characters/Warrior.png";
import archerImg from "./characters/Archer.png";
import magicianImg from "./characters/Magician.png";
import monster1 from "./monsters/monster1.png";
import monster2 from "./monsters/monster2.png";
import monster3 from "./monsters/monster3.png";

const levels = ["Beginner", "Medium", "Hard", "Geek"];
const characters = [
  { name: "Warrior", img: warriorImg },
  { name: "Archer", img: archerImg },
  { name: "Magician", img: magicianImg },
];

const monsterImages = [monster1, monster2, monster3];

function MathAdventure() {
  const [stage, setStage] = useState("selectCharacter");
  const [currentCharacter, setCurrentCharacter] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [hp, setHp] = useState(3);
  const [answer, setAnswer] = useState("");
  const [monsterImg, setMonsterImg] = useState(monster1);
  const [monsterEffect, setMonsterEffect] = useState(false);
  const [characterEffect, setCharacterEffect] = useState(false);

  useEffect(() => {
    if (stage === "play") {
      const randomIdx = Math.floor(Math.random() * monsterImages.length);
      setMonsterImg(monsterImages[randomIdx]);
    }
  }, [questionIndex, stage]);

  const generateQuestions = (level) => {
    const q = [];
    const random = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
    for (let i = 0; i < 5; i++) {
      let a, b, c, op, ans;
      switch (level) {
        case "Beginner":
          a = random(1, 10);
          b = random(1, 10);
          op = Math.random() > 0.5 ? "+" : "-";
          ans = op === "+" ? a + b : a - b;
          break;
        case "Medium":
          a = random(2, 12);
          b = random(2, 12);
          op = Math.random() > 0.5 ? "×" : "÷";
          ans = op === "×" ? a * b : a;
          if (op === "÷") a = a * b;
          break;
        case "Hard":
          a = random(5, 20);
          b = random(2, 10);
          c = random(1, 10);
          op = "mix";
          ans = a + b * c;
          break;
        case "Geek":
          a = random(1, 10);
          b = random(1, 10);
          op = "2x + 3y";
          ans = 2 * a + 3 * b;
          break;
        default:
          break;
      }
      q.push({ a, b, c, op, ans });
    }
    return q;
  };

  const handleCharacterSelect = (char) => {
    setCurrentCharacter(char);
    setStage("selectLevel");
  };

  const startGame = (level) => {
    setCurrentLevel(level);
    setQuestions(generateQuestions(level));
    setStage("play");
    setQuestionIndex(0);
    setScore(0);
    setHp(3);
    setAnswer("");
  };

  const currentQuestion = questions[questionIndex];

  const handleSubmit = () => {
    const isCorrect = parseInt(answer) === currentQuestion.ans;

    if (isCorrect) {
      setMonsterEffect(true);
      setScore(score + 1);
    } else {
      setCharacterEffect(true);
      setHp(prev => {
        const newHp = prev - 1;
        if (newHp <= 0) {
          setStage("gameOver");
        }
        return newHp;
      });
    }

    setTimeout(() => {
      setMonsterEffect(false);
      setCharacterEffect(false);
      setAnswer("");

      if (questionIndex + 1 < questions.length) {
        setQuestionIndex(questionIndex + 1);
      } else {
        setStage("result");
      }
    }, 800);
  };

  return (
    <div className="math-adventure-container">
      <h2 className="math-title">🧠 Math Adventure</h2>

      {stage === "selectCharacter" && (
        <div className="character-select">
          <h3>Choose Your Hero:</h3>
          <div className="character-grid side-by-side">
            {characters.map((char) => (
              <div
                key={char.name}
                className="character-card"
                onClick={() => handleCharacterSelect(char)}
              >
                <img src={char.img} alt={char.name} className="character-img" />
                <p>{char.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {stage === "selectLevel" && (
        <div className="level-select">
          <h3>Select Difficulty:</h3>
          {levels.map((level) => (
            <button
              key={level}
              className={`level-btn ${level.toLowerCase()}`}
              onClick={() => startGame(level)}
            >
              {level}
            </button>
          ))}
        </div>
      )}

      {stage === "play" && currentQuestion && (
        <div className="question-box">
          <img
            src={monsterImg}
            alt="monster"
            className={`monster-image ${monsterEffect ? "vibrate" : ""}`}
          />

<div className="character-wrapper">
  <img
    src={currentCharacter.img}
    alt="character"
    className={`character-image ${characterEffect ? "vibrate" : ""}`}
  />
  <div className="hp-bar">
    {Array.from({ length: hp }, (_, i) => (
      <span key={i} className="heart">❤️</span>
    ))}
  </div>
</div>


          <h3>Level: {currentLevel}</h3>
          <p>
            {currentLevel === "Geek"
              ? `What is 2 × ${currentQuestion.a} + 3 × ${currentQuestion.b}?`
              : currentLevel === "Hard"
              ? `What is ${currentQuestion.a} + ${currentQuestion.b} × ${currentQuestion.c}?`
              : `What is ${currentQuestion.a} ${currentQuestion.op} ${currentQuestion.b}?`}
          </p>

          <input
            type="number"
            className="answer-input"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
          <button className="submit-btn" onClick={handleSubmit}>Submit</button>
        </div>
      )}

{stage === "result" && (
  <div className="result-box">
    <h3>🎉 Great job, {currentCharacter.name}!</h3>
    <p>You finished the {currentLevel} Level!</p>
    <p>Score: {score} / {questions.length}</p>

    <button className="action-btn" onClick={() => setStage("selectCharacter")}>
      Play Again
    </button>

    {currentLevel !== "Geek" && (
      <button
        className="action-btn"
        onClick={() => {
          const nextLevelIndex = levels.indexOf(currentLevel) + 1;
          const nextLevel = levels[nextLevelIndex];
          startGame(nextLevel);
        }}
      >
        Challenge Next Level
      </button>
    )}
  </div>
)}


      {stage === "gameOver" && (
        <div className="gameover-box">
          <h3>💀 Game Over!</h3>
          <p>You ran out of HP.</p>
          <button onClick={() => setStage("selectCharacter")}>Try Again</button>
        </div>
      )}
    </div>
  );
}

export default MathAdventure;
